import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator as CalcIcon, Download, Loader2, Mail, Plus, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useProducts, useSettings } from "@/lib/api";
import { useApp } from "@/lib/store";
import { computeLine, dimsFromVariant, fmtW, newLine, summarize, totals } from "@/lib/calc";
import LineItemEditor from "@/components/site/LineItemEditor";
import { UnitToggle } from "@/components/site/common";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { track, useSeo } from "@/lib/seo";

function Card({ label, value, sub, testid }) {
  return <div className="card p-5"><p className="label">{label}</p><p data-testid={testid} className="serif text-3xl tabular">{value}</p>{sub && <p className="text-xs text-[var(--graphite)] mt-1">{sub}</p>}</div>;
}

function Fill({ label, pct, testid }) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-2"><span>{label}</span><span data-testid={testid} className="tabular">{pct.toFixed(1)}%</span></div>
      <div className="fill-bar"><span style={{ width: `${Math.min(100, pct)}%` }} /></div>
    </div>
  );
}

function AiAssist({ prods, onParsed }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const run = async () => {
    if (text.trim().length < 3) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/calculator/parse", { text });
      const lines = (data.items || []).map((it) => {
        const p = prods.find((x) => x.slug === it.product_slug);
        const unit = it.unit === "mm" ? "mm" : "inch";
        const base = newLine(p, { unit });
        const v = p.variants.find((x) => x.name === it.variant);
        const dims = Object.fromEntries(Object.entries(it.dims || {}).filter(([, val]) => val != null && val !== ""));
        return { ...base, ...(v ? { variant: v.name } : {}), dims: Object.keys(dims).length ? dims : v ? dimsFromVariant(v, unit) : base.dims, finish: p.finishes.includes(it.finish) ? it.finish : base.finish, quantity: it.quantity || "", qty_unit: ["pieces", "kg", "MT", "cartons"].includes(it.qty_unit) ? it.qty_unit : "pieces" };
      });
      if (lines.length) onParsed(lines);
      setMsg({ q: data.question, note: data.note, n: lines.length });
      track("calculator_ai_parse", { lines: lines.length });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-6" data-testid="ai-assist">
      <p className="eyebrow flex items-center gap-2"><Sparkles size={14} /> AI assist</p>
      <p className="serif text-2xl mt-2">Describe your requirement in plain words</p>
      <textarea data-testid="ai-input" rows={3} className="field mt-4" placeholder="e.g. 5,000 pcs 1/2 inch threaded rod, 3 ft long, HDG" value={text} onChange={(e) => setText(e.target.value)} />
      <button data-testid="ai-parse-button" onClick={run} disabled={busy} className="btn btn-outline btn-sm mt-3">{busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Fill the calculator</button>
      {msg && (
        <div data-testid="ai-result" className="mt-4 text-sm text-[var(--graphite)] border-l-2 border-[var(--brass)] pl-4 space-y-1">
          {msg.n > 0 && <p>Filled {msg.n} line{msg.n > 1 ? "s" : ""} below. The formula engine calculates all weights.</p>}
          {msg.note && <p>{msg.note}</p>}
          {msg.q && <p className="text-[var(--ink)]">{msg.q}</p>}
        </div>
      )}
    </div>
  );
}

function exportPdf(rows, t, sys) {
  import("jspdf").then(({ jsPDF }) => {
    const doc = new jsPDF();
    doc.setFont("times", "normal"); doc.setFontSize(20); doc.text("Tijiya Exports — Shipment Estimate", 14, 20);
    doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.text(new Date().toLocaleString(), 14, 27);
    let y = 40;
    rows.forEach((r) => { doc.text(`${r.product_name} ${r.variant} · ${r.finish} · ${r.quantity} pcs`, 14, y); doc.text(fmtW(r.net_kg, sys), 196, y, { align: "right" }); y += 7; });
    y += 6;
    [["Net weight", fmtW(t.net, sys)], ["Gross weight (incl. packaging)", fmtW(t.gross, sys)], ["Cartons", String(t.cartons)], ["Pallets", String(t.pallets)], ["20 ft container (by weight)", `${t.pct20.toFixed(1)}%`], ["40 ft container (by weight)", `${t.pct40.toFixed(1)}%`]].forEach(([k, v]) => { doc.text(k, 14, y); doc.text(v, 196, y, { align: "right" }); y += 7; });
    y += 8; doc.setFontSize(9); doc.text("Estimates are indicative. Final weights are confirmed in our quotation.", 14, y);
    doc.save("tijiya-shipment-estimate.pdf");
  });
}

export default function Calculator() {
  const { data: prods = [] } = useProducts();
  const { data: s } = useSettings();
  const { sys } = useApp();
  const nav = useNavigate();
  const [lines, setLines] = useState([]);
  const [mail, setMail] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const cfg = s?.calculator;
  useSeo("Shipment Weight Calculator", "Estimate the weight, cartons, pallets and container load of your steel hardware order.");
  useEffect(() => { if (prods.length && !lines.length) setLines([newLine(prods.find((p) => p.slug.startsWith("threaded-rod")) || prods[0])]); }, [prods, lines.length]);
  const all = lines;
  const results = useMemo(() => all.map((l) => computeLine(prods.find((p) => p.slug === l.product_slug), l, cfg)), [all, prods, cfg]);
  const t = totals(results, cfg);
  const update = (i, nl) => setLines(all.map((x, j) => (j === i ? nl : x)));
  const rows = all.map((l, i) => ({ product_name: prods.find((p) => p.slug === l.product_slug)?.name || "", variant: l.variant, finish: l.finish, quantity: results[i]?.pcs || 0, net_kg: results[i]?.net || 0 }));
  const valid = all.some((l, i) => l.product_slug && results[i]?.pcs > 0);

  const toEnquiry = () => {
    try { sessionStorage.setItem("tij_calc_lines", JSON.stringify(all.filter((l) => l.product_slug))); } catch { /* ignore */ }
    track("calculator_used", { action: "send_enquiry" });
    nav("/enquiry?from=calculator");
  };
  const sendMail = async () => {
    setSending(true);
    try { await api.post("/calculator/email", { email, lines: rows, totals: { net: t.net, gross: t.gross, cartons: t.cartons, pallets: t.pallets, pct20: t.pct20, pct40: t.pct40 } }); toast.success("Estimate sent to your inbox"); setMail(false); track("calculator_used", { action: "email" }); }
    catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div data-testid="calculator-page">
      <section className="wrap pt-16 pb-10 border-b border-[var(--stone)] flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-4 flex items-center gap-2"><CalcIcon size={14} /> Tool</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl">Shipment Weight Calculator</h1>
          <p className="mt-5 text-[var(--graphite)] max-w-2xl md:text-lg">How much will your order weigh, and roughly how many pallets or containers is that? Weights use steel density of {cfg?.density || 7850} kg/m³ and our catalogue data.</p>
        </div>
        <UnitToggle />
      </section>
      <section className="wrap py-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-5">
          <AiAssist prods={prods} onParsed={setLines} />
          {all.map((l, i) => (
            <LineItemEditor key={l.key} idx={i} line={l} products={prods} onChange={(nl) => update(i, nl)} onRemove={all.length > 1 ? () => setLines(all.filter((_, j) => j !== i)) : null}>
              {results[i] && <p data-testid={`line-${i}-result`} className="mt-4 text-sm text-[var(--graphite)] tabular border-t border-[var(--stone)] pt-3">{fmtW(results[i].wpp, sys)} per piece ({results[i].source}) · {results[i].pcs.toLocaleString()} pcs · net {fmtW(results[i].net, sys)}</p>}
            </LineItemEditor>
          ))}
          <button data-testid="calc-add-line" className="btn btn-outline btn-sm" onClick={() => setLines([...all, newLine(prods[0])])}><Plus size={14} /> Add line item</button>
        </div>
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 space-y-4" data-testid="calc-results">
            <div className="grid grid-cols-2 gap-4">
              <Card label="Weight per piece" testid="calc-wpp" value={results[0] ? fmtW(results[0].wpp, sys) : "—"} sub="Line 01" />
              <Card label="Total net weight" testid="calc-net" value={fmtW(t.net, sys)} sub={`${t.pcs.toLocaleString()} pcs`} />
              <Card label="Gross incl. packaging" testid="calc-gross" value={fmtW(t.gross, sys)} />
              <Card label="Cartons · Pallets" testid="calc-packing" value={`${t.cartons} · ${t.pallets}`} />
            </div>
            <div className="card p-6 space-y-5">
              <p className="label">Container estimate (by weight)</p>
              <Fill label={`20 ft · ${(+cfg?.container_20 || 21000).toLocaleString()} kg payload`} pct={t.pct20} testid="calc-pct20" />
              <Fill label={`40 ft · ${(+cfg?.container_40 || 26000).toLocaleString()} kg payload`} pct={t.pct40} testid="calc-pct40" />
              {t.gross > 0 && <p data-testid="calc-summary" className="serif text-xl">{summarize(t)}</p>}
            </div>
            <p className="text-xs text-[var(--graphite)]">Estimates are indicative. Final weights are confirmed in our quotation.</p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="calc-send-enquiry" disabled={!valid} onClick={toEnquiry} className="btn btn-brass"><Send size={15} /> Send this as an enquiry</button>
              <button data-testid="calc-download-pdf" disabled={!valid} onClick={() => { exportPdf(rows, t, sys); track("calculator_used", { action: "pdf" }); }} className="btn btn-outline"><Download size={15} /> PDF</button>
              <button data-testid="calc-email-open" disabled={!valid} onClick={() => setMail(true)} className="btn btn-outline"><Mail size={15} /> Email me</button>
            </div>
          </div>
        </aside>
      </section>
      <Dialog open={mail} onOpenChange={setMail}>
        <DialogContent className="bg-[var(--paper)]">
          <DialogTitle className="serif text-3xl font-medium">Email me this estimate</DialogTitle>
          <input data-testid="calc-email-input" type="email" className="field" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button data-testid="calc-email-send" disabled={sending || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)} onClick={sendMail} className="btn btn-brass">{sending && <Loader2 size={14} className="animate-spin" />} Send estimate</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
